import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { sriSyncJobApi } from "./axios";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import { AxiosError } from "axios";

export type SriSyncJobTipo = "recibidos" | "retenciones" | "emitidos" | "anulados";

export type SriSyncJobStatus =
  | "PENDING"
  | "RUNNING"
  | "WAITING_LOGIN"
  | "WAITING_CAPTCHA"
  | "COMPLETED"
  | "FAILED";

export interface SriSyncJobI {
  id: string;
  clientId: number;
  tipo: SriSyncJobTipo;
  ano: string | null;
  mes: string | null;
  fechaDesde: string | null;
  fechaHasta: string | null;
  status: SriSyncJobStatus;
  resultado: { encontrados: number; nuevos: number } | null;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CrearSriSyncJobPayload {
  tipo: SriSyncJobTipo;
  ano?: string;
  mes?: string;
  fechaDesde?: string;
  fechaHasta?: string;
}

const handleAuthError = (err: unknown): never => {
  const obj = (err as AxiosError).response;
  if (obj?.status === 401) LogoutSystem();
  throw new Error((obj?.data as Error)?.message ?? "Error desconocido");
};

export const crearSriSyncJob = async (
  payload: CrearSriSyncJobPayload,
): Promise<SriSyncJobI> => {
  try {
    const t = ValidateCurrentToken();
    if (!t) throw new Error("TOKEN INVALIDO");
    const { data } = await sriSyncJobApi.post("/", payload, {
      headers: { Authorization: t },
    });
    return data.data;
  } catch (err) {
    return handleAuthError(err);
  }
};

export const listarSriSyncJobs = async (): Promise<SriSyncJobI[]> => {
  try {
    const t = ValidateCurrentToken();
    if (!t) throw new Error("TOKEN INVALIDO");
    const { data } = await sriSyncJobApi.get("/", {
      headers: { Authorization: t },
    });
    return data.data ?? [];
  } catch (err) {
    return handleAuthError(err);
  }
};

/**
 * Hook principal del módulo. La lista de jobs hace polling automático
 * cada 4s — así el estado (RUNNING, WAITING_LOGIN, WAITING_CAPTCHA,
 * COMPLETED, FAILED) se actualiza solo en pantalla, sin que el usuario
 * tenga que refrescar manualmente mientras el agente trabaja en segundo
 * plano en su propia PC.
 */
export const useSriSyncJobs = () => {
  const queryClient = useQueryClient();

  const jobsQuery = useQuery(["sri-sync-jobs"], listarSriSyncJobs, {
    refetchInterval: 4000,
    refetchOnWindowFocus: false,
  });

  const crearJobMutation = useMutation(crearSriSyncJob, {
    onSuccess: () => {
      queryClient.invalidateQueries(["sri-sync-jobs"]);
    },
  });

  return { jobsQuery, crearJobMutation };
};